<template>
    <HeadMetaGame noindex />
    <div
        v-if="game"
        :class="$style.export_game"
    >
        <iframe
            ref="iframeRef"
            :class="$style.iframe"
            :title="game.title"
            :src="gameSrc"
        />
        <template v-if="showMobileInterface">
            <AdFlipCards
                v-if="showMobileInterface"
                :adType="adWidgetType"
                :class="$style.ad_block"
            >
                <template #front>
                    <StickyGameBanner
                        :game="game"
                        :categories="gameCatInfo"
                    />
                </template>
            </AdFlipCards>
            <DragnDropClose
                v-if="isPortal"
                :startPosition="14"
                @click="closeGame"
            />
            <RotateBanner
                v-if="!rotateBannerClosed"
                :orientation="orientation"
                :class="$style.rotateBanner"
                @close="onMessageClose"
            />
        </template>
    </div>
    <ErrorContent
        v-else-if="pageStatus !== 200"
        :randomGameHRU="null"
        :type="pageStatus"
    />
</template>
<script lang="ts" setup>
import { onBeforeUnmount, onMounted, onServerPrefetch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { DEFAULT_CLIDS } from '@/constants/general'
import { type PageStatus, useAppStore } from '@/store/app-store'
import { MainFrameService } from '@/modules/main-frame'
import { useAdsStore } from '@/store/ads-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import ErrorContent from '@/components_new/ErrorContent/ErrorContent.vue'
import HeadMetaGame from '@/pages/Games/components/HeadMetaGame.vue'
import RotateBanner from '@/pages/Games/components/RotateBanner/RotateBanner.vue'
import DragnDropClose from '@/pages/Games/components/DragnDropClose.vue'
import AdFlipCards from './components/AdFlipCards.vue'
import StickyGameBanner from './components/StickyGameBanner.vue'
import { useGamePageStore } from './game-store'
import { UserService } from '@/modules/user-service'
import { createExportApiClient, DestroyExportApiFunction } from '@/modules/export-api'
import { useConfig } from '@/composables/config'
import { createSafeStorage } from '@/modules/safe-storage'
import { createInGamePayments } from '@/modules/in-game-payments'
import { createCloudSave, getCloudSaveApi } from '@/modules/cloud-saves'
import { addRedirectCounter } from '@/router/query-params'

const PAYLOAD_PARAM_NAME = 'payload'

const gamePageStore = useGamePageStore()
const appStore = useAppStore()
const { pageStatus } = storeToRefs(appStore)
const { game, gameCatInfo } = storeToRefs(gamePageStore)

const route = useRoute()
const initialized = ref(false)
const rotateBannerClosed = ref(false)
const gameSrc = ref('')
const iframeRef = ref<HTMLIFrameElement>()

const mainFrameService = ref<MainFrameService>()
const destroyExportApiClient = ref<DestroyExportApiFunction>()
const adsStore = useAdsStore()
const loggerStore = useLoggerStore()
const userStore = useUserStore()
const exportMode = computed(() => appStore.exportMode)
const isPortal = computed(() => exportMode.value === 'portal')
const showMobileInterface = computed(
    () => appStore.platform !== 'desktop' && game.value && (isPortal.value || exportMode.value === 'widget'),
)
const adWidgetType = computed(() => (exportMode.value === 'widget' ? 'widget_sticky_mobile' : 'sticky_mobile'))

async function updateData(routeParams: string) {
    if (!game || !game.value || game.value.hru !== routeParams) {
        gamePageStore.resetGameData()
        await gamePageStore.setGame(routeParams)
    }
}

function ping() {
    if (!document.hidden && document.hasFocus()) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'ping',
        })
    }
}

const { VUE_APP_GAME_PING_INTERVSAL_MS } = useConfig()
const pingIntervalRef = ref<ReturnType<typeof setInterval> | null>(null)
const handleCloseGame = ref<() => void>()

function closeGame() {
    handleCloseGame.value?.()
}

const orientation = computed(() => {
    const result = [] as ('horizontal' | 'vertical')[]
    if (game.value?.vertical) {
        result.push('vertical')
    }
    if (game.value?.horizontal) {
        result.push('horizontal')
    }
    return result
})

function onMessageClose() {
    rotateBannerClosed.value = true
}

onMounted(async () => {
    if (pageStatus.value !== 200) {
        return
    }

    if (!game.value) {
        // dev-mode
        await updateData(route.params.game as string)
    }

    // TODO: make this more transparent
    // but should never happen, otherwise `new URL(game.value.game_url)` would cause errors
    if (!game.value) {
        console.error('Error: No game found')
        return
    }

    if (!adsStore.adMediator) {
        console.error('Error: No ad service was passed to the main frame')
        return
    }

    adsStore.updateTargeting({ content_category: game.value.categories, provider: game.value.source || '' })

    adsStore.adMediator.preloadIntroAds()

    const payload = route.query[PAYLOAD_PARAM_NAME] || ''

    const url = new URL(game.value.game_url)

    // this logic is only needed for playhop — they give us statistic based on clids
    // but the clids should be one of given by them, and not our default "direct" or "direct-test"
    if (appStore.clid && !Object.values(DEFAULT_CLIDS).includes(appStore.clid)) {
        url.searchParams.set('clid', appStore.clid as string)
    }
    if (payload) {
        url.searchParams.set('payload', payload as string)
    }

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'game_opened',
    })

    let { authorizeUser, getUser, chargeUser } = userStore

    if (exportMode.value === 'portal') {
        const { client, destroy } = await createExportApiClient()
        authorizeUser = client.authorizeUser
        getUser = client.getUser
        chargeUser = client.chargeUser
        destroyExportApiClient.value = destroy
        handleCloseGame.value = client.closeGame
    }

    gameSrc.value = url.href

    initialized.value = true

    mainFrameService.value = !adsStore.adMediator
        ? undefined
        : new MainFrameService({
              iframeElement: iframeRef.value,
              prerollDisabled: appStore.widgetId !== undefined,
              adMediator: adsStore.adMediator,
              inGamePaymentsMessageHandler: createInGamePayments(game.value.hru, chargeUser),
              storageMessageHandler: createSafeStorage(game.value.hru, game.value.id, getUser),
              cloudSaveMessageHandler: createCloudSave(game.value.id, game.value.hru),
              userService: new UserService({ authorizeUser, getUser }),
              onLogin: async () => {
                  if (!game.value) {
                      return
                  }
                  const api = getCloudSaveApi(game.value.id)

                  const save = await api.getState()

                  if (Object.keys(save).length === 0) {
                      return
                  }

                  addRedirectCounter(url)
                  gameSrc.value = url.href
              },
          })

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'game',
        action: 'init',
        gameProvider: game.value.source,
        gameCategories: game.value.categories,
    })

    pingIntervalRef.value = setInterval(() => {
        ping()
    }, Number(VUE_APP_GAME_PING_INTERVSAL_MS))
})

onBeforeUnmount(() => {
    mainFrameService.value?.destroy()
    destroyExportApiClient.value?.()
    clearInterval(pingIntervalRef.value || 0)
})

onServerPrefetch(async () => {
    try {
        await gamePageStore.setGame(route.params.game as string)
    } catch (e) {
        // no throwing here, we handle errors manually
        if (typeof e === 'number') {
            appStore.setPageStatus(e as PageStatus)
        } else {
            appStore.setPageStatus(500)
        }
    }
})
</script>
<style module>
.rotateBanner {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translate(-50%, 0);
}
.export_game {
    display: grid;
    grid-template-rows: 1fr auto;
    height: 100vh;
}
.iframe {
    width: 100%;
    height: 100%;
}

.ad_block {
    width: 100%;
    height: 50px;
}

@media (--mobile-landscape), (--tablet-plus) {
    .ad_block {
        display: none;
    }
}
</style>
